import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { YOUTUBE_SUGGESTED_PRIVACY, type CampaignLaunchQuestion } from 'fym-shared/src/music24/campaignLaunch';
import {
  GOOGLE_PRIVACY_URL,
  YOUTUBE_TERMS_URL,
  youtubeSettingsFromDraft,
  type YouTubePostSettings,
  type YouTubePrivacyStatus,
  type YouTubeSettingsDraft,
} from 'fym-shared/src/music24/youtubePostSettings';
import { TypedMemo } from '../../../../../../common/types';
import { useGetPromoChannelsQuery } from '../../../../../../../redux/api/fymAPI';
import { youtubeMessages } from '../../../../composer/youtube/youtubeMessages';
import { YouTubeSettingsFields } from '../../../../composer/youtube/YouTubeSettingsFields';
import { launchMessages } from './launchMessages';
import { withBrandWord } from '../../../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from '../Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LaunchYouTubeQuestionProps {
  accountId: number;
  question: CampaignLaunchQuestion;
  /** The answer, or null while a choice is missing. */
  onAnswer: (settings: YouTubePostSettings | null) => void;
}

function termsLink(parts: React.ReactNode[]): React.ReactNode {
  return (
    <a href={YOUTUBE_TERMS_URL} target="_blank" rel="noreferrer">
      {parts}
    </a>
  );
}

function privacyLink(parts: React.ReactNode[]): React.ReactNode {
  return (
    <a href={GOOGLE_PRIVACY_URL} target="_blank" rel="noreferrer">
      {parts}
    </a>
  );
}

/**
 * The YouTube row of the launch questions. Who can see the videos: the stored default of the
 * channel is selected, else "Public"; the user can change it, and the answer is the new default.
 * "Made for kids" has no selected value, for every campaign: it is never stored and never taken
 * from an earlier answer (owner decision 2026-10-11; YouTube wants the uploader to declare it).
 */
const LaunchYouTubeQuestionComponent: React.FC<LaunchYouTubeQuestionProps> = ({ accountId, question, onAnswer }) => {
  const intl = useIntl();
  const { data: channels } = useGetPromoChannelsQuery({ accountId });
  const storedPrivacy =
    channels?.channels.find((channel) => channel.platform === 'youtube')?.postingDefaults?.youtube?.privacyStatus ??
    null;
  // Null until the user picks: a default that loads late still shows.
  const [pickedPrivacy, setPickedPrivacy] = useState<YouTubePrivacyStatus | null>(null);
  const [madeForKids, setMadeForKids] = useState<boolean | null>(null);
  const draft = useMemo<YouTubeSettingsDraft>(
    () => ({ privacyStatus: pickedPrivacy ?? storedPrivacy ?? YOUTUBE_SUGGESTED_PRIVACY, madeForKids }),
    [madeForKids, pickedPrivacy, storedPrivacy]
  );
  useEffect(() => {
    onAnswer(youtubeSettingsFromDraft(draft));
  }, [draft, onAnswer]);

  return (
    <section className={styles.launchQuestion} data-testid="launch-question-youtube">
      <h3 className={styles.launchQuestionHead}>
        {intl.formatMessage(launchMessages.questionPosts, { name: 'YouTube', count: question.postCount })}
      </h3>
      <YouTubeSettingsFields
        draft={draft}
        idPrefix="launch-youtube"
        onPrivacy={setPickedPrivacy}
        onMadeForKids={setMadeForKids}
      />
      <p className={styles.launchNote}>{intl.formatMessage(launchMessages.youtubeRemember)}</p>
      <p className={styles.launchNote}>
        {withBrandWord(intl.formatMessage(youtubeMessages.consent, { terms: termsLink, privacy: privacyLink }))}
      </p>
    </section>
  );
};

export const LaunchYouTubeQuestion = TypedMemo(LaunchYouTubeQuestionComponent);
